import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { layers } from '../data/portfolio';
import './Hero.css';

const STAGE = 560; // design size of the exploded-stack drawing, in px

// Scales the fixed-size stack drawing down to fit narrow screens.
const useStageScale = () => {
  const ref = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => {
      setScale(Math.min(1, entry.contentRect.width / STAGE));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, scale];
};

const Hero = () => {
  const [selected, setSelected] = useState(0);
  const [stageRef, scale] = useStageScale();
  const current = layers[selected];

  return (
    <>
      <section className="hero wrap">
        <motion.div
          className="hero-copy"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <div className="hero-meta mono">
            <span>Full Stack Developer</span>
            <span>10.68° N, 122.95° E</span>
            <span>Bacolod City, PH · UTC+8</span>
          </div>

          <h1 className="hero-title display">
            One{' '}<br />developer.{' '}<br />
            <span className="accent">Every</span>{' '}<br />
            <span className="outline-text">layer.</span>
          </h1>

          <p className="hero-lede">
            I’m <strong>Benjo M. Abrasado</strong>. I build web apps with Laravel, Node.js,
            Angular and React, cross-platform mobile apps with Flutter, and custom WordPress
            plugins — from the database all the way up to the thumb.
          </p>

          <div className="hero-actions">
            <a href="#work" className="btn btn--accent">See the work ↓</a>
            <a href="#contact" className="btn">Start a project</a>
          </div>
        </motion.div>

        <motion.div
          id="stack"
          className="stack"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
        >
          <div className="stack-caption mono">
            <span>Fig. 1 — Exploded view</span>
            <span>Tap a layer</span>
          </div>

          <div className="stack-fit" ref={stageRef} style={{ height: STAGE * scale }}>
            <div className="stack-stage" style={{ transform: `scale(${scale})` }}>
              {layers.map((layer, i) => {
                const on = i === selected;
                return (
                  <button
                    key={layer.num}
                    type="button"
                    className={`plate ${on ? 'is-on' : ''}`}
                    style={{ top: i * 92 + (on ? -26 : 0), zIndex: 10 - i }}
                    onClick={() => setSelected(i)}
                    aria-label={`Layer ${layer.num}: ${layer.name} — ${layer.tools}`}
                    aria-pressed={on}
                  >
                    <span className="plate-num">{layer.num}</span>
                    <span className="plate-name mono">{layer.name}</span>
                    <span className="plate-tools mono">{layer.tools}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </motion.div>
      </section>

      <section className="readout-section wrap" aria-live="polite">
        <div className="readout">
          <div className="readout-label">
            <span className="eyebrow">Layer {current.num} / 04</span>
            <span className="readout-name">{current.name}</span>
            <span className="readout-tools mono">{current.tools}</span>
          </div>
          <div className="readout-body">
            <p className="readout-desc">{current.desc}</p>
            <ul className="readout-features mono">
              {current.features.map((feature) => (
                <li key={feature}>{feature}</li>
              ))}
            </ul>
            <div className="readout-chips" role="group" aria-label="Choose a layer">
              {layers.map((layer, i) => (
                <button
                  key={layer.num}
                  type="button"
                  className="chip"
                  aria-pressed={i === selected}
                  onClick={() => setSelected(i)}
                >
                  {layer.num} · {layer.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Hero;
